import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import "./TestFeedBackModal.scss";
import { url_263, url_267 } from "../../../../../custom_utilities/api_services";

const TestFeedBackModal = ({
  show,
  paperId,
  paperType,
  userId,
  token,
  tokenType,
  handleCloseFeedbackModal,
}) => {
  const [formTitle, setFormTitle] = useState("");
  const [formFields, setFormFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [canShowModal, setCanShowModal] = useState(false);

  // Fetch form fields from API when modal opens
  useEffect(() => {
    const fetchFormFields = async () => {
      if (show) {
        try {
          const response = await fetch(
            `${url_263}?paper_type=${paperType}&paper_id=${paperId}&user_id=${userId}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );
          const data = await response.json();
          if (data.status && data.form_data) {
            setFormTitle(data.form_data.form_title || "Share your Feedback");
            setFormFields(data.form_data.form_fields || []);

            const initialFormData = {};
            data.form_data.form_fields.forEach((field) => {
              initialFormData[field.id] =
                field.field_type === "textarea" ? "" : null;
            });
            setFormData(initialFormData);
            setCanShowModal(true);
          } else {
            alert(data.message);
            setCanShowModal(false);
            handleCloseFeedbackModal();
          }
        } catch (error) {
          alert(error.message);
          setCanShowModal(false);
          // handleFeedBackModal();
        }
      } else {
        setCanShowModal(false); // reset when modal closed
      }
    };

    fetchFormFields();
  }, [show, paperId, paperType, userId]);

  // Handle form input changes
  const handleChange = (fieldId, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prepare the payload for the API
    const payload = {
      user_id: userId,
      paper_id: paperId,
      paper_type: paperType, // statically giving 5 ( as 5 is for exams)
      responses: formFields.map((field) => ({
        id: field.id,
        response: formData[field.id],
        ...(field.id === 4 &&
          formData[field.id] === "Yes" && {
            sub_field_data: formData[`${field.id}_details`] || "",
          }),
      })),
    };

    const response = await fetch(url_267, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (response.ok) {
      handleCloseFeedbackModal();
    } else {
      handleCloseFeedbackModal();
    }
  };

  // Check if all required fields are filled
  const isFormValid = () => {
    return formFields.every((field) => {
      const value = formData[field.id];
      switch (field.field_type) {
        case "number": // Rating fields (IDs 1, 5)
          return value !== null && value >= 1 && value <= 5;

        case "radio": // Yes/No fields (IDs 2, 4)
          return value === "Yes" || value === "No";

        case "text": // Difficulty field (ID 3)
          return ["Easy", "Moderate", "Hard"].includes(value);

        case "textarea": // Suggestions field (ID 6)
          return true; // Optional field, always valid

        default:
          return false;
      }
    });
  };

  // Render form field based on field_type
  const renderField = (field) => {
    switch (field.field_type) {
      case "number": // Rating field
        return (
          <div className="rating-buttons">
            {field.options.map((option) => (
              <Button
                key={option.value}
                onClick={() => handleChange(field.id, parseInt(option.value))}
                style={{ backgroundColor: option.color, border: "none" }}
                className={`rating-btn ${
                  formData[field.id] === parseInt(option.value)
                    ? "selected"
                    : ""
                }`}
              >
                {option.value}
              </Button>
            ))}
          </div>
        );

      case "radio": // Yes/No field
        return (
          <div>
            <div className="yes-no-buttons">
              {field.options.map((option) => (
                <div
                  key={option.value}
                  className={`yes-no-btn ${
                    formData[field.id] === option.value ? "selected" : ""
                  }`}
                  onClick={() => handleChange(field.id, option.value)}
                >
                  <span className="circle"></span>
                  {option.value}
                </div>
              ))}
            </div>
            {/* Show textarea when Yes is selected for out-of-syllabus question */}
            {field.id === 4 && formData[field.id] === "Yes" && (
              <Form.Control
                as="textarea"
                rows={3}
                className="mt-3"
                value={formData[`${field.id}_details`] || ""}
                onChange={(e) =>
                  handleChange(`${field.id}_details`, e.target.value)
                }
                placeholder="Write here"
              />
            )}
          </div>
        );

      case "text": // Difficulty field
        return (
          <div className="difficulty-buttons">
            {field.options.map((option) => (
              <Button
                key={option.value}
                onClick={() => handleChange(field.id, option.value)}
                style={{ backgroundColor: option.color, border: "none" }}
                className={`difficulty-btn ${
                  formData[field.id] === option.value ? "selected" : ""
                }`}
              >
                {option.value}
              </Button>
            ))}
          </div>
        );

      case "textarea": // Suggestions field
        return (
          <Form.Control
            as="textarea"
            rows={3}
            value={formData[field.id] || ""}
            onChange={(e) => handleChange(field.id, e.target.value)}
            placeholder={field.placeholder || "Enter your suggestions here..."}
          />
        );

      default:
        return null;
    }
  };
  if (!show || !canShowModal) return null;

  return (
    <Modal
      centered
      dialogClassName="test-feedback-modal"
      style={{ zIndex: 99999999999999 }}
      show={show}
    >
      <Modal.Header>
        <Modal.Title className="test-feedback-title">{formTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="test-feedback-modal-body">
        <Form onSubmit={handleSubmit}>
          {formFields.map((field) => (
            <Form.Group key={field.id} className="mb-3">
              <Form.Label
                dangerouslySetInnerHTML={{ __html: field.field_label }}
              />
              {renderField(field)}
            </Form.Group>
          ))}
          <Button
            type="submit"
            className="submit-btn"
            disabled={!isFormValid()}
          >
            Submit Feedback
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default TestFeedBackModal;
